import { type DefaultRootState } from 'react-redux';
import {
  isRejectedWithValue,
  type Middleware,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { selectAuthStatus } from '@bbkAdminRedux/rtkq/auth-utils';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { internalBbkApiSlice } from '@bbkAdminRedux/rtkq/internal.bbkApi.slice';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import {
  currentRelativeUrl,
  hardRedirect,
} from '@bbkAdminUtils/browser-navigation';

// One-way guard so several concurrent 401s do not each set the alert / trigger
// navigation. No reset is needed: a full-page navigation is imminent and will
// tear down this module.
let redirectingToSignIn = false;

const getRejectedStatus = (action: UnknownAction) => {
  const payload =
    'payload' in action && typeof action.payload === 'object'
      ? action.payload
      : undefined;

  if (!payload) return undefined;

  if ('originalStatus' in payload && typeof payload.originalStatus === 'number')
    return payload.originalStatus;

  if ('status' in payload && typeof payload.status === 'number') {
    return payload.status;
  }

  return undefined;
};

const isAuthenticatedApiAction = (action: UnknownAction) =>
  typeof action.type === 'string' &&
  (action.type.startsWith(`${bbkApiSlice.reducerPath}/`) ||
    action.type.startsWith(`${internalBbkApiSlice.reducerPath}/`));

/**
 * On a mid-session 401 from an authenticated API call, treat the lost session
 * as a hard browser boundary: clear local token state, set a "session ended"
 * alert, and reload to the sign-in page (carrying the current location as
 * `redirectUri`). The reload wipes the store and all in-memory tenant caches,
 * so there is no cross-principal window and no durable 401 state machine to
 * maintain. An already-expired session needs no CBIDP server logout, so this
 * intentionally does not dispatch the async sign-out mutation.
 */
export const unauthorizedRedirectMiddleware: Middleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    // Read our signed-in belief BEFORE reducing the action. A current-user 401
    // is itself an authenticated-API action, and `next(action)` flips the probe
    // to unauthenticated; reading after would make the middleware skip the
    // redirect for exactly the 401 that ends the session. Derived from the
    // /api/current_user probe. Mid-session the probe is fulfilled, so a scoped
    // 401 still redirects; on a signed-out boot the probe is still loading (not
    // authenticated), so we do not loop.
    const wasAuthenticated =
      selectAuthStatus(getState() as DefaultRootState) === 'authenticated';

    const result = next(action);

    if (redirectingToSignIn) return result;
    if (!isRejectedWithValue(action)) return result;
    if (!isAuthenticatedApiAction(action)) return result;
    if (!wasAuthenticated) return result;
    if (getRejectedStatus(action) !== 401) return result;

    redirectingToSignIn = true;
    BrightbackSession.setAuthAlert('Please log in again for security');
    BrightbackSession.setAccessToken(null, 0);
    BrightbackSession.setRefreshToken(null);
    BrightbackSession.setAccessTokenType(null);

    hardRedirect(
      `${LogInPaths.signIn}?${new URLSearchParams({
        redirectUri: currentRelativeUrl(),
      }).toString()}`
    );

    return result;
  };
